import { ExpandMore } from '@mui/icons-material';
// Components
import {
  FormControl,
  Radio,
  FormControlLabel,
  RadioGroup,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormLabel,
  TextField,
  Typography,
  Box
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { ImageUpload } from '../../../components';
import clsx from 'clsx';
import React, { FC, useEffect, useState } from 'react';
import { IWorkItem } from '../../../models';

interface IMobileWorkItems {
  original: IWorkItem;
  setCurrentImage: (val: string) => void;
  isDisabled?: boolean;
  handleImageUpload: (file: File, workItem: IWorkItem) => Promise<void>;
  isSavingImage: { [id: number]: boolean };
  isAllAccordionsExpanded?: boolean;
  updateMyData: (workItem: IWorkItem, columnId: string, value: any) => void;
}

export const MobileWorkItems: FC<IMobileWorkItems> = ({
  original,
  setCurrentImage,
  isDisabled,
  handleImageUpload,
  isSavingImage,
  isAllAccordionsExpanded,
  updateMyData
}) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(true);
  const { description, category, meetsStandards, note, workItemId, hasImage } = original;

  const handleAccordionChange = (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setIsOpen(isExpanded);
  };

  useEffect(() => {
    if (isAllAccordionsExpanded !== undefined) {
      setIsOpen(isAllAccordionsExpanded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllAccordionsExpanded]);

  return (
    <Accordion className={classes.root} expanded={isOpen} onChange={handleAccordionChange}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">
            <span className={clsx(classes.boldName, classes.subLabel)}>{description}</span>
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Description:
          </FormLabel>
          <Typography component="span" variant="body2">
            {description || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Category:
          </FormLabel>
          <Typography component="span" variant="body2">
            {category || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Meets Standards:
          </FormLabel>
          {isDisabled ? (
            <Typography component="span" variant="body2">
              {meetsStandards ? 'Yes' : 'No'}
            </Typography>
          ) : (
            <FormControl fullWidth variant="outlined" size="small" required>
              <RadioGroup
                row
                value={meetsStandards !== null ? (meetsStandards ? 'Yes' : 'No') : null}
                onChange={e => updateMyData(original, 'meetsStandards', e.target.value === 'Yes')}
                aria-label="meetsStandards"
                name="meetsStandards"
              >
                <FormControlLabel
                  classes={{
                    label: classes.checkboxLabel
                  }}
                  value="Yes"
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  classes={{
                    label: classes.checkboxLabel
                  }}
                  value="No"
                  control={<Radio />}
                  label="No"
                />
              </RadioGroup>
            </FormControl>
          )}
        </div>
        <div className={classes.photoWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Photo:
          </FormLabel>
          <Box display="flex" alignItems="flex-start">
            {!isDisabled && (
              <Box marginRight={2.5}>
                <FormLabel className={classes.uploadLabel}>Upload Image</FormLabel>
                <ImageUpload
                  imageKey={`workitem-${workItemId}`}
                  disabled={isSavingImage[workItemId] || false}
                  showSource={false}
                  showLabel={false}
                  handleFileChange={(_, file) => {
                    handleImageUpload(file, original);
                  }}
                />
              </Box>
            )}
            {hasImage && (
              <Box display="flex" flexDirection="column">
                <FormLabel className={classes.uploadLabel} style={{ whiteSpace: 'nowrap' }}>
                  View Image
                </FormLabel>
                <picture
                  className={classes.pictureImages}
                  onClick={() =>
                    setCurrentImage(
                      `${window.location.origin}/api/purchase-order-checklists/work-items/${workItemId}/image`
                    )
                  }
                >
                  <img
                    key={`workitem-image-${workItemId}-${original.imageTimestamp || 'initial'}`}
                    className={classes.colorImage}
                    src={`${window.location.origin}/api/purchase-order-checklists/work-items/${workItemId}/image`}
                    alt=""
                  />
                </picture>
              </Box>
            )}
          </Box>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Note:
          </FormLabel>
          {isDisabled ? (
            <>{note}</>
          ) : (
            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              autoComplete="nope"
              label=""
              name="quantity"
              size="small"
              value={note || ''}
              onChange={e => updateMyData(original, 'note', e.target.value)}
            />
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    marginBottom: '0.5rem',
    '&:first-child': { marginTop: 0 }
  },
  photoWrapper: {
    marginBottom: '0',
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  pictureImages: {
    userSelect: 'none',
    cursor: 'pointer',
    padding: '1rem'
  },
  colorImage: {
    width: 50,
    height: 50
  },
  checkboxLabel: {
    fontSize: theme.typography.body2.fontSize,
    position: 'relative',
    top: '.1rem'
  },
  uploadLabel: {
    display: 'block',
    marginBottom: '2px',
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.text.primary
  }
}));
